import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import type { BankOfferMetrics } from 'services/types/metrics';
import { NAVY, RADIUS_ITEM, SURFACE_CONTAINER_LOW, YELLOW } from 'styles/tokens';

import ChartCard from './ChartCard';

interface BankOffersFunnelProps {
  metrics?: BankOfferMetrics;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  periodLabel: string;
  /** Opens the leads list filtered by the bank offer state the clicked step represents. */
  onNavigate?: (bankOfferStatus: string) => void;
}

interface Stage {
  label: string;
  value: number;
  /** Copy for the conversion caption above this stage. Absent on the first stage. */
  fromLabel?: string;
  /** BankOfferRequest status this step maps to, used to drill into the leads list. */
  bankOfferStatus: string;
}

/** Wide enough for the longest stage label ("SOLICITUDES CREADAS") without wrapping. */
const STAGE_LABEL_WIDTH = 172;
const NAV_COLUMN_WIDTH = 26;

const navButtonSx = {
  width: 22,
  height: 22,
  color: alpha(NAVY, 0.5),
  backgroundColor: 'transparent',
  transition: 'color 0.15s ease, background-color 0.15s ease',
  '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) },
} as const;

const NavButton: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Tooltip title={`Ver leads · ${label}`} arrow>
    <IconButton size="small" onClick={onClick} aria-label={`Ver leads de ${label}`} sx={navButtonSx}>
      <ManageSearchIcon sx={{ fontSize: 15 }} />
    </IconButton>
  </Tooltip>
);

/**
 * A request can be sent to the bank without ever being pre-approved, so "Enviadas al banco" is not
 * a subset of "Pre-aprobadas". When that happens the step-over-step rate is meaningless and we fall
 * back to the share of all created requests.
 */
const conversionCaption = (stage: Stage, previousValue: number, created: number): string =>
  stage.value <= previousValue
    ? `↓ ${pct(stage.value, previousValue)}% de las ${stage.fromLabel}`
    : `${pct(stage.value, created)}% de las solicitudes creadas`;

const pct = (count: number, total: number): number => (total > 0 ? Math.round((count / total) * 1000) / 10 : 0);

const FunnelSkeleton: FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {[100, 62, 38].map((width) => (
      <Box key={width}>
        <Skeleton variant="text" width={160} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={28} width={`${width}%`} sx={{ borderRadius: RADIUS_ITEM }} />
      </Box>
    ))}
  </Box>
);

const ContextBlock: FC<{ label: string; value: string; hint?: string; onNavigate?: () => void }> = ({
  label,
  value,
  hint,
  onNavigate,
}) => (
  <Box sx={{ flex: 1, minWidth: 160, p: 2, backgroundColor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_ITEM }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 600,
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: alpha(NAVY, 0.5),
        }}
      >
        {label}
      </Typography>
      {onNavigate && <NavButton label={label} onClick={onNavigate} />}
    </Box>
    <Typography
      sx={{
        fontFamily: 'Jost, sans-serif',
        fontWeight: 700,
        fontSize: '1.35rem',
        color: NAVY,
        lineHeight: 1.2,
        mt: 0.5,
      }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: alpha(NAVY, 0.55), mt: 0.25 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

/**
 * Big Idea: how much of the bank offer demand survives to pre-approval and to being sent to the bank.
 * Descending horizontal bars per design-system §19.8; the stage entered through the largest drop
 * carries the single YELLOW preattentive accent.
 */
const BankOffersFunnel: FC<BankOffersFunnelProps> = ({
  metrics,
  isLoading,
  hasError,
  onRetry,
  periodLabel,
  onNavigate,
}) => {
  const funnel = metrics?.funnel;
  const created = funnel?.created ?? 0;

  const stages: Stage[] = [
    { label: 'Solicitudes creadas', value: created, bankOfferStatus: 'created' },
    {
      label: 'Pre-aprobadas',
      value: funnel?.preApproved ?? 0,
      fromLabel: 'solicitudes creadas',
      bankOfferStatus: 'pre_approved',
    },
    {
      label: 'Enviadas al banco',
      value: funnel?.sentToBank ?? 0,
      fromLabel: 'pre-aprobadas',
      bankOfferStatus: 'sent_to_bank',
    },
  ];

  // The stage reached through the steepest fall is the one worth acting on.
  const dropByStage = stages.map((stage, index) => (index === 0 ? -1 : (stages[index - 1].value || 0) - stage.value));
  const highlightedIndex = dropByStage.indexOf(Math.max(...dropByStage.slice(1)));

  const preApprovalRate = pct(stages[1].value, created);
  const sentRate = pct(stages[2].value, created);
  const backlog = metrics?.commercialBacklog;

  const body = (): ReactNode => {
    if (isLoading) return <FunnelSkeleton />;

    if (hasError) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.7), mb: 1.5 }}>
            No pudimos cargar el embudo de ofertas bancarias.
          </Typography>
          <Button
            onClick={onRetry}
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: RADIUS_ITEM,
              color: NAVY,
              backgroundColor: alpha(NAVY, 0.06),
              '&:hover': { backgroundColor: alpha(NAVY, 0.1) },
            }}
          >
            Reintentar
          </Button>
        </Box>
      );
    }

    if (created === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.7) }}>
            No hay solicitudes de oferta en este período. Amplía el rango de fechas o revisa los filtros de origen.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {stages.map((stage, index) => {
            const isHighlighted = index === highlightedIndex;
            const widthPct = created > 0 ? Math.min((stage.value / created) * 100, 100) : 0;
            const caption = index === 0 ? null : conversionCaption(stage, stages[index - 1].value, created);

            return (
              <Box key={stage.label}>
                {caption !== null && (
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '0.75rem',
                      fontWeight: isHighlighted ? 600 : 400,
                      color: isHighlighted ? NAVY : alpha(NAVY, 0.5),
                      mb: 1,
                      ml: 0.25,
                    }}
                  >
                    {caption}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: 28,
                      borderRadius: RADIUS_ITEM,
                      backgroundColor: SURFACE_CONTAINER_LOW,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${widthPct}%`,
                        backgroundColor: isHighlighted ? YELLOW : alpha(NAVY, index === 0 ? 0.9 : 0.55),
                        borderRadius: RADIUS_ITEM,
                        transition: 'width 0.46s cubic-bezier(0.25, 0.8, 0.25, 1)',
                      }}
                    />
                  </Box>

                  {/* Fixed widths so the values, labels and drill-down icons line up in columns
                      instead of trailing labels of different lengths. */}
                  <Box sx={{ width: STAGE_LABEL_WIDTH, flexShrink: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        color: NAVY,
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {stage.value.toLocaleString()}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontSize: '0.75rem',
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: alpha(NAVY, 0.55),
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {stage.label}
                    </Typography>
                  </Box>
                  <Box sx={{ width: NAV_COLUMN_WIDTH, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {onNavigate && stage.value > 0 && (
                      <NavButton label={stage.label} onClick={() => onNavigate(stage.bankOfferStatus)} />
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3.5 }}>
          <ContextBlock
            label="Rechazadas por el banco"
            value={(metrics?.rejectedTotal ?? 0).toLocaleString()}
            hint={`${pct(metrics?.rejectedTotal ?? 0, created)}% de las solicitudes creadas`}
            onNavigate={
              onNavigate && (metrics?.rejectedTotal ?? 0) > 0 ? () => onNavigate('rejected_by_bank') : undefined
            }
          />
          <ContextBlock
            label="Pre-aprobadas sin enviar"
            value={(backlog?.preApprovedNotSentCount ?? 0).toLocaleString()}
            hint="Backlog comercial pendiente de envío"
            onNavigate={
              onNavigate && (backlog?.preApprovedNotSentCount ?? 0) > 0
                ? () => onNavigate('pre_approved_pending')
                : undefined
            }
          />
        </Box>
      </>
    );
  };

  const title =
    created > 0
      ? `${preApprovalRate}% de las solicitudes llega a pre-aprobación y ${sentRate}% termina enviada al banco`
      : 'Embudo de ofertas bancarias';

  return (
    <ChartCard
      eyebrow="Ofertas bancarias"
      title={title}
      description="Recorrido de cada solicitud desde que se crea hasta que se envía al banco."
      footer={
        periodLabel
          ? `${periodLabel} · Solicitudes creadas en el período · Cada etapa cuenta las solicitudes que la alcanzaron alguna vez`
          : undefined
      }
    >
      {body()}
    </ChartCard>
  );
};

export default BankOffersFunnel;
